import React, { useMemo } from "react";
import {
  Typography,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Button,
  CircularProgress,
} from "@material-ui/core";
import { i18n } from "../../translate/i18n";

// Set GLOBAL de datos de ejemplo para el preview (botón "Ver con datos de ejemplo").
// Claves = conceptos de variable (mismos nombres que el texto libre).
export const EXAMPLE_VALUES = {
  nombre: "María",
  apellido: "Gómez",
  profesional: "Dr. Gazzera",
  fecha: "martes 29/07",
  hora: "14:00",
  servicio: "corte de pelo",
  minutos_respuesta: "15",
};

// Renderiza el body de una plantilla reemplazando cada {{n}} por una FICHA:
// por defecto el nombre del concepto ([nombre], [servicio]…); con showExample,
// el valor de ejemplo. El texto fijo se muestra normal. Read-only.
const renderPreview = (body, varOrder, showExample) => {
  if (!body) return null;
  const parts = body.split(/(\{\{\s*\d+\s*\}\})/g);
  return parts.map((seg, i) => {
    const m = seg.match(/\{\{\s*(\d+)\s*\}\}/);
    if (!m) return <span key={i}>{seg}</span>;
    const n = Number(m[1]);
    const concept = (varOrder && varOrder[n - 1]) || `var${n}`;
    const label = showExample ? (EXAMPLE_VALUES[concept] ?? concept) : `[${concept}]`;
    return (
      <Chip
        key={i}
        size="small"
        label={label}
        style={{
          height: 20,
          margin: "0 2px",
          fontFamily: showExample ? "inherit" : "monospace",
          fontSize: 12,
          backgroundColor: showExample ? "#e8f5e9" : "#e3f2fd",
          color: showExample ? "#2e7d32" : "#0b5394",
        }}
      />
    );
  });
};

const statusLabel = (st) =>
  st === "APPROVED" ? i18n.t("templateOverrides.stApproved")
  : st === "PENDING" ? i18n.t("templateOverrides.stPending")
  : st === "REJECTED" ? i18n.t("templateOverrides.stRejected")
  : st;
const statusColor = (st) =>
  st === "APPROVED" ? "#2e7d32" : st === "PENDING" ? "#ed6c02" : st === "REJECTED" ? "#d32f2f" : "#9e9e9e";

// ─────────────────────────────────────────────────────────────────────────────
// Bloque Meta de UN kind: selector de override + preview con fichas.
// props:
//   kindData: { kind, label, defaultName, varCount, varOrder, override }
//   approved: { applicable, templates:[{name,variableCount,category,status,bodyText}], canonical:{[name]:{bodyText,varOrder,category}} }
//   showExample: bool   saving: bool   conditionLabel?: string
//   onSave(kind, { templateName|null, variableCount|null })
// ─────────────────────────────────────────────────────────────────────────────
const TurnosMetaTemplateBlock = ({ kindData, approved, showExample, saving, onSave, conditionLabel }) => {
  const templates = useMemo(() => approved?.templates || [], [approved]);
  const canonical = approved?.canonical || {};

  const compatible = templates.filter((t) => t.variableCount === kindData.varCount);
  const incompatible = templates.filter((t) => t.variableCount !== kindData.varCount);

  const value = kindData.override || "";
  const activeName = kindData.override || kindData.defaultName;

  const real = templates.find((t) => t.name === activeName);
  const canon = canonical[activeName];
  const previewBody = real?.bodyText || canon?.bodyText || null;
  const previewStatus = real ? real.status : canon ? "canonical" : null;
  const previewCategory = real?.category || canon?.category || null;
  const isMarketing = previewCategory === "MARKETING";
  const varCountOf = (name) => (templates.find((x) => x.name === name) || {}).variableCount;

  return (
    <div style={{ marginTop: 8, paddingTop: 8, borderTop: conditionLabel ? "1px dashed #ddd" : "none" }}>
      {conditionLabel && (
        <Typography variant="caption" style={{ display: "block", fontWeight: 700, color: "#555", marginBottom: 4 }}>
          {conditionLabel}
        </Typography>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
        <Typography variant="caption" color="textSecondary">{i18n.t("templateOverrides.useLabel")}</Typography>
        <FormControl variant="outlined" size="small" style={{ minWidth: 240 }}>
          <Select
            value={value}
            displayEmpty
            onChange={(e) => {
              const name = e.target.value;
              if (name === "") onSave(kindData.kind, { templateName: null, variableCount: null });
              else onSave(kindData.kind, { templateName: name, variableCount: varCountOf(name) });
            }}
          >
            <MenuItem value="">
              <em>{i18n.t("templateOverrides.useDefault")} ({kindData.defaultName})</em>
            </MenuItem>
            {compatible.map((t) => (
              <MenuItem key={t.name} value={t.name}>
                {t.name}{t.category === "MARKETING" ? " · MARKETING" : ""}
              </MenuItem>
            ))}
            {incompatible.map((t) => (
              <MenuItem key={t.name} value={t.name} disabled>
                {t.name} — {t.variableCount} {i18n.t("templateOverrides.vars")} ({i18n.t("templateOverrides.incompatible")})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {saving && <CircularProgress size={16} />}
        {value && (
          <Button size="small" onClick={() => onSave(kindData.kind, { templateName: null, variableCount: null })} disabled={saving}>
            {i18n.t("templateOverrides.backToDefault")}
          </Button>
        )}
      </div>

      {/* Preview con fichas */}
      {approved?.applicable && previewBody && (
        <div style={{ background: "#fff", border: "1px solid #e6e6ef", borderRadius: 6, padding: "8px 10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 600 }}>{activeName}</span>
            {previewStatus === "canonical" ? (
              <Chip size="small" variant="outlined" label={i18n.t("templateOverrides.notCreated")} style={{ height: 18, borderColor: "#ed6c02", color: "#ed6c02", fontSize: 11 }} />
            ) : (
              <Chip size="small" label={statusLabel(previewStatus)} style={{ height: 18, backgroundColor: statusColor(previewStatus), color: "#fff", fontSize: 11, fontWeight: 600 }} />
            )}
            {previewCategory && <Chip size="small" variant="outlined" label={previewCategory} style={{ height: 18, fontSize: 11 }} />}
          </div>
          <Typography variant="body2" component="div" style={{ whiteSpace: "pre-wrap", lineHeight: 2 }}>
            {renderPreview(previewBody, kindData.varOrder, showExample)}
          </Typography>
          {isMarketing && (
            <Typography variant="caption" style={{ display: "block", color: "#d32f2f", marginTop: 4 }}>
              ⚠ {i18n.t("templateOverrides.marketingWarning")}
            </Typography>
          )}
        </div>
      )}
      {approved?.applicable && !previewBody && (
        <Typography variant="caption" color="textSecondary">{i18n.t("templateOverrides.noPreview")}</Typography>
      )}
      {approved && !approved.applicable && (
        <Typography variant="caption" color="textSecondary">
          {approved.reason === "qr_line" ? i18n.t("templateOverrides.qrLine") : i18n.t("templateOverrides.noLine")}
        </Typography>
      )}
      {compatible.length === 0 && approved?.applicable && (
        <Typography variant="caption" style={{ display: "block", color: "#ed6c02", marginTop: 2 }}>
          {i18n.t("templateOverrides.noneCompatible")}
        </Typography>
      )}
    </div>
  );
};

export default TurnosMetaTemplateBlock;
